import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SF_SURV_RSPN_LIST_SRCH, SG_SURV_RSPN_LIST } from "./services/PtliSurvRspnMgmtService";
import { PTLI0418001Q } from "../qustnr/PTLI0418001Q";
import { PTLI0419001Q } from "../ques/PTLI0419001Q";
import { PTLI0420001Q } from "../itm/PTLI0420001Q";

/*
 * Survey Response Result List
 * !== 설문 응답 결과 목록 ==!
 */
export const PTLI0421001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0060", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survRspnListSrch: useForm({
            defaultSchema: SF_SURV_RSPN_LIST_SRCH,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survRspnList: useGrid({
            defaultSchema: SG_SURV_RSPN_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getSurvRspnList: useFetch({
            api: (page = grid.survRspnList.page) =>
                APIS.getSurvRspnList(form.survRspnListSrch.getValues(), page, grid.survRspnList.size),
            enabled: comnUtils.isEmpty(form.survRspnListSrch.errors),
            key: [grid.survRspnList.page, grid.survRspnList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.survRspnListSrch.getValues(),
                    page: grid.survRspnList.page,
                    size: grid.survRspnList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };
    const render = {
        cell: {
            qustnrTtle: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`${URLS.ptli0421002q}/${rowValues.quesRspnUsrId}`}>{value}</Link>;
            },
        },
    };
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onSrchBtnClick: () => {
            grid.survRspnList.setPage(0);
            fetch.getSurvRspnList.fetch(0);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survRspnListSrch.schema.bsopClsfCd} />
                            <Group.Control {...form.survRspnListSrch.schema.ptlTpCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.survRspnListSrch.schema.qustnrTtle}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survRspnListSrch.setValue("qustnrTtle", item.qustnrTtle);
                                                form.survRspnListSrch.setValue("qestnrId", item.qestnrId);
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0418001Q callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                            <Group.Control
                                {...form.survRspnListSrch.schema.quesCn}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survRspnListSrch.setValue("quesCn", item.quesCn);
                                                form.survRspnListSrch.setValue("quesId", item.quesId);
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0419001Q callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.survRspnListSrch.schema.itmCn}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survRspnListSrch.setValue("itmCn", item.itmCn);
                                                form.survRspnListSrch.setValue("quesItmId", item.quesItmId);
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0420001Q callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                            <Group.Control {...form.survRspnListSrch.schema.rgsrUsr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survRspnListSrch.schema.cstmSctnOrgzCd} />
                            <Group.Control {...form.survRspnListSrch.schema.cstmUnitOrgzCd} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button
                                role={"reset"}
                                onClick={() => {
                                    form.survRspnListSrch.reset();
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.survRspnList.grid}
                            data={fetch.getSurvRspnList.data?.survRspnList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
